import { useState, useEffect } from 'react';
import HeaderPreview from "../../GeneratePdf/HeaderPreview";
import FooterPreview from "../../GeneratePdf/FooterPreview";
import { applyFullTieOrder } from '../../../utils/quadrantTieOrder';


// Image imports
import flow_CP from '../../../Assets/Images/LeadershipFlowImg/flow_CP.png';
import flow_CS from '../../../Assets/Images/LeadershipFlowImg/flow_CS.png';
import flow_CV from '../../../Assets/Images/LeadershipFlowImg/flow_CV.png';
import flow_PC from '../../../Assets/Images/LeadershipFlowImg/flow_PC.png';
import flow_PS from '../../../Assets/Images/LeadershipFlowImg/flow_PS.png';
import flow_PV from '../../../Assets/Images/LeadershipFlowImg/flow_PV.png';
import flow_SC from '../../../Assets/Images/LeadershipFlowImg/flow_SC.png';
import flow_SP from '../../../Assets/Images/LeadershipFlowImg/flow_SP.png';
import flow_SV from '../../../Assets/Images/LeadershipFlowImg/flow_SV.png';
import flow_VC from '../../../Assets/Images/LeadershipFlowImg/flow_VC.png';
import flow_VP from '../../../Assets/Images/LeadershipFlowImg/flow_VP.png';
import flow_VS from '../../../Assets/Images/LeadershipFlowImg/flow_VS.png';


export default function LeadershipFlow({ apiData, pageNumber }) {
  const [leadershipSnapshotResults, setLeadershipSnapshotResults] = useState([])
  const [leadershipLabels, setLeadershipLabels] = useState([])
  const [mostSelecTedStarter, setMostSelecTedStarter] = useState('')
  const [highestGroup, setHighestGroup] = useState("collaborative");


  const styles = {
    heading: { fontFamily: "Helvetica, sans-serif", fontWeight: 700, fontStyle: "normal", fontSize: "14px", lineHeight: "18px", letterSpacing: "0px", textTransform: "uppercase", color: "#00577D", margin: 0, padding: "16px 0px" },
    paragraph: { fontFamily: "Helvetica, sans-serif", fontWeight: 400, fontStyle: "normal", fontSize: "10px", lineHeight: "14px", letterSpacing: "0px", color: "#000000", margin: "0 0 16px 0" }
  };

    // Flow content mapping
  const FLOW_CONTENT = {
    'CS': {
      title: 'You flow from Collaborative to Strategic',
      details: [
        'When Collaborative Leaders grow into Strategic Leadership, they take their people skills and apply them to clear planning, organization, and alignment. You still care deeply about your team. Now you help them understand the plan, the structure, and how decisions fit together.',
        'Your awareness of team needs helps you spot obstacles early and design plans that work in real conditions. This creates a leadership style that is both organized and people-focused. The result is smoother coordination, clearer priorities, and a team that knows how their work fits into the bigger picture.'
      ]
    },
    'CV': {
      title: 'You flow from Collaborative to Visionary',
      details: [
        'When Collaborative Leaders grow into Visionary Leadership, they shift from creating a supportive, people-first environment to inspiring others with a bigger picture. You still lead through trust and connection. Now you help people see beyond today’s tasks and understand the larger impact of their work.',
        'Your relational strengths expand into a broader sense of purpose. You bring people together around a shared direction and explain why the work matters. This blend of care and purpose motivates the team and strengthens commitment to long-term goals.'
      ]
    },
    'CP': {
      title: 'You flow from Collaborative to Performance',
      details: [
        'When Collaborative Leaders grow into Performance Leadership, they bring their relational strengths into a more results-focused approach. You are skilled at creating environments where people feel valued. Now you also want to turn that positive culture into measurable progress.',
        'You set specific goals, raise standards, and direct the team toward real outcomes. You support people while also challenging them and keeping momentum high. Your understanding of team dynamics helps you push others toward excellence without losing connection along the way.'
      ]
    },
    'SC': {
      title: 'You flow from Strategic to Collaborative',
      details: [
        'When Strategic Leaders grow into Collaborative Leadership, they shift from directing to involving. You still create structure and clarity, but now you pay more attention to team dynamics and individual contributions.',
        'You invite others into discussions, value their input, and design plans that reflect the strengths and needs of the group. This balance produces strong execution supported by genuine buy-in.'
      ]
    },
    'SV': {
      title: 'You flow from Strategic to Visionary',
      details: [
        'When Strategic Leaders grow into Visionary Leadership, they combine practical planning with deeper purpose and direction. You continue to think systematically, but now you also focus on meaning, long-term impact, and inspiration.',
        'You connect roles and tasks to a larger story so the team understands why their work matters. This creates a culture that values both clarity and purpose.'
      ]
    },
    'SP': {
      title: 'You flow from Strategic to Performance',
      details: [
        'When Strategic Leaders grow into Performance Leadership, they channel their planning abilities into focused execution and measurable outcomes. You still value structure and strategy, but now your attention shifts to speed, efficiency, and results.',
        'You break strategies into concrete steps, manage resources effectively, and drive progress with discipline. This helps the team move from planning to consistent performance.'
      ]
    },
    'VS': {
      title: 'You flow from Visionary to Strategic',
      details: [
        'When Visionary Leaders grow into Strategic Leadership, they begin grounding their big ideas and creativity in practical, actionable plans. You still think in terms of purpose and long-term impact. Now you also focus on the systems, structures, and data needed to turn those ideas into reality.',
        'You keep your inspirational energy, but you balance it with planning, priorities, and risk awareness. This combination helps your vision become both inspiring and achievable.'
      ]
    },
    'VC': {
      title: 'You flow from Visionary to Collaborative',
      details: [
        'When Visionary Leaders grow into Collaborative Leadership, they combine their innovative ideas with a more inclusive, team-centered approach. You recognize that your vision becomes stronger when others help shape it.',
        'You create space for open communication and shared ownership. This approach builds trust and ensures that people feel connected to the direction of the work, not just informed about it.'
      ]
    },
    'VP': {
      title: 'You flow from Visionary to Performance',
      details: [
        'When Visionary Leaders grow into Performance Leadership, they connect their bold ideas to concrete results. You start translating the big picture into clear goals, real actions, and disciplined follow-through.',
        'You continue to inspire, but you now focus on execution and measurable progress. This blend of imagination and action brings your ideas to life in a way that feels both creative and practical for the team.'
      ]
    },
    'PC': {
      title: 'You flow from Performance to Collaborative',
      details: [
        'When Performance Leaders grow into Collaborative Leadership, they expand from focusing mainly on results to valuing teamwork and shared ownership. You begin balancing high standards with openness, communication, and feedback.',
        'You involve people in decisions and recognize individual strengths. This shift helps create a team culture where people feel respected, supported, and motivated to contribute.'
      ]
    },
    'PV': {
      title: 'You flow from Performance to Visionary',
      details: [
        'When Performance Leaders grow into Visionary Leadership, they direct their energy and drive toward larger, transformative goals. You start thinking beyond immediate results and consider the long-term impact of your work.',
        'You articulate a compelling future, encourage creativity, and give your team a sense of purpose behind their efforts. This brings new meaning to the fast-paced action you excel at.'
      ]
    },
    'PS': {
      title: 'You flow from Performance to Strategic',
      details: [
        'When Performance Leaders grow into Strategic Leadership, they take their results-driven mindset and apply it to long-term planning and organizational design. You still value execution, but now you think in terms of systems, priorities, and sustainable growth.',
        'You help the team understand not just what needs to get done, but why it matters for the future. This shift turns strong performers into leaders who can build lasting structures and guide the organization forward.'
      ]
    }
  }

  const LABELS = ['strategic', 'performance', 'visionary', 'collaborative'];
  const LABEL_SHORT = { strategic: 'S', performance: 'P', visionary: 'V', collaborative: 'C' };
  const FLOW_IMAGES = {
    'CP': flow_CP,
    'CS': flow_CS,
    'CV': flow_CV,
    'PC': flow_PC,
    'PS': flow_PS,
    'PV': flow_PV,
    'SC': flow_SC,
    'SP': flow_SP,
    'SV': flow_SV,
    'VC': flow_VC,
    'VP': flow_VP,
    'VS': flow_VS,
  };


  const leadershipShort = {
    strategic: 'S',
    performance: 'P',
    visionary: 'V',
    collaborative: 'C'
  };



  useEffect(() => {
    // Map variables
    if (!apiData?.variables || !Array.isArray(apiData.variables)) return;

    const variableMap = apiData.variables.reduce((acc, el) => {
      acc[el.variable] = el.score;
      return acc;
    }, {});

    if (Object.keys(variableMap).length === 0) return;

    // Extract scores
    const performance_r1_m = variableMap["per_round1_m"] || 0;
    const strategic_r1_m = variableMap["strat_round1_m"] || 0;
    const visionary_r1_m = variableMap["vis_round1_m"] || 0;
    const collaborative_r1_m = variableMap["col_round1_m"] || 0;

    const performance_r2_m = variableMap["per_round2_m"] || 0;
    const strategic_r2_m = variableMap["strat_round2_m"] || 0;
    const visionary_r2_m = variableMap["vis_round2_m"] || 0;
    const collaborative_r2_m = variableMap["col_round2_m"] || 0;

    const performance_r1_l = variableMap["per_round1_l"] || 0;
    const strategic_r1_l = variableMap["strat_round1_l"] || 0;
    const visionary_r1_l = variableMap["vis_round1_l"] || 0;
    const collaborative_r1_l = variableMap["col_round1_l"] || 0;

    const performance_r2_l = variableMap["per_round2_l"] || 0;
    const strategic_r2_l = variableMap["strat_round2_l"] || 0;
    const visionary_r2_l = variableMap["vis_round2_l"] || 0;
    const collaborative_r2_l = variableMap["col_round2_l"] || 0;

    // Highest, second highest, least, second least
    let most_score = Math.max(performance_r1_m, strategic_r1_m, visionary_r1_m, collaborative_r1_m);
    let second_most_score = Math.max(performance_r2_m, strategic_r2_m, visionary_r2_m, collaborative_r2_m) / 2;
    let least_score = Math.max(performance_r1_l, strategic_r1_l, visionary_r1_l, collaborative_r1_l);
    let second_least_score = Math.max(performance_r2_l, strategic_r2_l, visionary_r2_l, collaborative_r2_l) / 2;

    if (least_score >= second_least_score) {
      least_score = second_least_score - 1;
    }

    const total = most_score + second_most_score + least_score + second_least_score;

    const most_per = (most_score / total) * 100;
    const second_most_per = (second_most_score / total) * 100;
    const least_per = (least_score / total) * 100;
    const second_least_per = (second_least_score / total) * 100;

    // Labels for mapping
    const labels_arr = ["performance", "strategic", "visionary", "collaborative"];

    const mostValues = [performance_r1_m, strategic_r1_m, visionary_r1_m, collaborative_r1_m];
    const secondMostValues = [performance_r2_m, strategic_r2_m, visionary_r2_m, collaborative_r2_m];
    const leastValues = [performance_r1_l, strategic_r1_l, visionary_r1_l, collaborative_r1_l];
    const secondLeastValues = [performance_r2_l, strategic_r2_l, visionary_r2_l, collaborative_r2_l];

    const most_score_label = Math.max(...mostValues);
    const second_most_score_label = Math.max(...secondMostValues);
    const least_score_label = Math.max(...leastValues);
    const second_least_score_label = Math.max(...secondLeastValues);

    const getLabel = (values, maxValue) => labels_arr[values.indexOf(maxValue)];

    const most_label = getLabel(mostValues, most_score_label);
    const second_most_label = getLabel(secondMostValues, second_most_score_label);
    const least_label = getLabel(leastValues, least_score_label);
    const second_least_label = getLabel(secondLeastValues, second_least_score_label);

    const getPercentageOrder = (label) => {
      if (label === most_label?.toLowerCase()) return most_per;
      if (label === second_most_label?.toLowerCase()) return second_most_per;
      if (label === least_label?.toLowerCase()) return least_per;
      if (label === second_least_label?.toLowerCase()) return second_least_per;
      return 0;
    };

    // Order: ["performance", "collaborative", "visionary", "strategic"]
    const results = [
      getPercentageOrder("performance"),
      getPercentageOrder("collaborative"),
      getPercentageOrder("visionary"),
      getPercentageOrder("strategic")
    ];

    setLeadershipSnapshotResults(results);
    setLeadershipLabels(["performance", "collaborative", "visionary", "strategic"]);
    // setLeadershipLabels(["strategic", "visionary", "performance", "collaborative"]);
    setMostSelecTedStarter(most_label);
  }, [apiData]);

  const flowContent = getFlowContent(leadershipSnapshotResults, leadershipLabels);

  function getFlowContent(leadershipSnapshotResults, leadershipLabels) {
    if (!leadershipSnapshotResults || !leadershipLabels || leadershipSnapshotResults.length !== 4 || leadershipLabels.length !== 4) {
      return FLOW_CONTENT['CS']; // Default fallback
    }

    const pairs = leadershipLabels.map((label, i) => ({
      label: label.toLowerCase(),
      score: leadershipSnapshotResults[i]
    }));
    const sorted = applyFullTieOrder([...pairs].sort((a, b) => b.score - a.score), (p) => p.label, (p) => p.score);
    const highest = sorted[0].label;
    const secondHighest = sorted[1].label;
    const flowKey = `${leadershipShort[highest]}${leadershipShort[secondHighest]}`;

    return FLOW_CONTENT[flowKey] || FLOW_CONTENT['CS']; // Fallback to CS if not found
  }




  function getLeadershipFlowImage(leadershipSnapshotResults, leadershipLabels) {
    if (!leadershipSnapshotResults || !leadershipLabels || leadershipSnapshotResults.length !== 4 || leadershipLabels.length !== 4) return null;
    const pairs = leadershipLabels.map((label, i) => ({
      label: label.toLowerCase(),
      score: leadershipSnapshotResults[i]
    }));
    const sorted = applyFullTieOrder([...pairs].sort((a, b) => b.score - a.score), (p) => p.label, (p) => p.score);
    const highest = sorted[0].label;
    const secondHighest = sorted[1].label;
    const flowKey = `${leadershipShort[highest]}${leadershipShort[secondHighest]}`;
    return FLOW_IMAGES[flowKey];
  }


  return (
    <div>
      <HeaderPreview />
      <div className="">
        <h4 style={styles.heading}>LEADERSHIP FLOW</h4>
        <p style={styles.paragraph}>
          Just like you have a Starter Quadrant and a Flow Type™, your leadership has a natural starting point and a movement pattern. The problem is that most leaders over-rely on one style. That strength becomes a limitation, while other leadership styles go underused or unrecognized.
        </p>
        <p style={styles.paragraph}>
          Your Leadership Flow shows how you lead under pressure, how you make decisions, and how you influence others. Often, your third and fourth styles hold untapped potential that sits beneath an overdeveloped primary strength.
        </p>
        <p style={styles.paragraph}>Teams and organizations need all four styles to thrive: Collaborative, Visionary, Strategic, and Performance Leadership. When even one is missing, alignment and performance begin to suffer.</p>
        <p style={styles.paragraph}>
          <span style={{ fontWeight: "700", paddingBottom: "5px" }}> Expand Your Range: </span>Lead beyond your default style.<br />
          <span style={{ fontWeight: "700", paddingBottom: "5px" }}>Diagnose the Gaps: </span> Identify what is missing in your team.<br />
          <span style={{ fontWeight: "700" }}> Reclaim Hidden Strengths: </span>Activate strengths that have been underused.
        </p>
        <p style={styles.paragraph}>You do not have to be all things at once. You do need access to it all.</p>
      </div>
      <h4 style={{ fontFamily: "Helvetica, sans-serif", fontWeight: 700, fontStyle: "normal", fontSize: "11px", lineHeight: "15px", letterSpacing: "0px", color: "#00577D", margin: 0, marginBottom: "16px" }}>{flowContent.title}</h4>
      <div style={{ display: 'flex', gap: '16px', alignItems: 'start' }}>
        {leadershipSnapshotResults.length === 4 && leadershipLabels.length === 4 && (
          <img src={getLeadershipFlowImage(leadershipSnapshotResults, leadershipLabels)} alt="Leadership Flow" style={{ width: '108px', height: '108px', flexShrink: 0 }} />
        )}
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'start', }} >
          <p style={{ fontFamily: "Helvetica, sans-serif", fontWeight: 400, fontStyle: "normal", fontSize: "10px", lineHeight: "14px", letterSpacing: "0px", color: "#000000", margin: 0, marginBottom: "16px" }} >
            {flowContent.details[0]}
          </p>
          <p style={{ fontFamily: "Helvetica, sans-serif", fontWeight: 400, fontStyle: "normal", fontSize: "10px", lineHeight: "14px", letterSpacing: "0px", color: "#000000", margin: 0 }} >
            {flowContent.details[1]}
          </p>
        </div>
      </div>
      <FooterPreview pageNumber={pageNumber} />
    </div>
  )
}
